<template>
  <div>
    <transition name="fade">
      <div v-if="active" class="fixed w-screen h-screen inset-0 z-80000 bg-gray-950/30" @click="cancel">
        <div v-if="active" class="w-[calc(100%_-_2rem)] sm:w-5/6 md:w-3/4 mx-auto max-w-2xl mt-24 bg-white/70 rounded-2xl overflow-hidden border border-white/20 backdrop-blur-xl dark:bg-gray-900" @click.prevent.stop>
          <div class="w-full relative flex items-center px-5 space-x-6 py-4">
            <div class="flex items-center pointer-events-none">
              <o-icon icon="search" :size="16" class="text-gray-800 dark:text-gray-100" />
            </div>
            <input
              ref="search"
              :value="query"
              type="text"
              class="flex-grow text-gray-800 text-lg bg-transparent outline-none dark:text-gray-100 dark:bg-transparent"
              @input="evt=>query=evt.target.value"
              @keyup.esc="cancel"
              @keyup.up="moveIndex(-1)"
              @keyup.down="moveIndex(1)"
              @keyup.enter="select"
            >
            <div class="flex items-center space-x-2">
              <o-loader v-if="state.filtering" size="16" />
              <!--<o-chip v-else class="mb-0 opacity-75" flat outline>
                Esc
              </o-chip>-->
              <o-button size="slim" flat icon="x" @click="cancel" />
            </div>
          </div>
          <div :class="{'hidden': !query.length}" class="w-full bg-white/30 border-t border-black/5 overflow-x-hidden dark:bg-gray-950/10 dark:border-white/10" style="max-height: calc(100vh - 400px)">
            <o-scroll>
              <div v-for="(model, name, index) in models" :key="index">
                <template v-if="model.results && model.results.length">
                  <div class="py-3 px-4 flex items-center justify-between">
                    <p class="text-base font-medium text-gray-700 dark:text-gray-200">
                      {{ model.name }}
                    </p>

                    <o-chip v-if="model.loading || model.results.length" flat class="mb-0">
                      <o-loader v-if="model.loading" size="16px" />
                      <template v-if="model.results.length">
                        {{ model.results.length }}
                      </template>
                    </o-chip>
                  </div>

                  <div v-if="model.results.length" class="p-2">
                    <div
                      v-for="(result, key) in model.results"
                      :key="key"
                      class="mb-1 px-3 py-2 cursor-pointer transition duration-100 east-in-out rounded-md"
                      :class="state.selected.model === index && state.selected.result === key ? 'bg-gray-200/45 text-gray-800 dark:bg-white/10 dark:text-gray-100' : 'text-gray-800 dark:text-gray-100'"
                      @mouseenter="setIndex(index, key)"
                    >
                      <component
                        :is="model.component"
                        :result="result"
                        @click="active = false"
                        @close="active = false"
                      />
                    </div>
                  </div>

                  <div v-if="!model.results.length && query" class="text-gray-700 py-3 px-4 no-results dark:text-gray-200">
                    <div class="flex items-center">
                      <o-icon icon="warning" :size="16" class="mr-4 text-red-600" />
                      <span>{{ $t('components.search.search.noResults') }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </o-scroll>
          </div>
          <div class="hidden md:flex bg-white/20 px-4 py-2 items-center space-x-3 text-gray-600 text-sm border-t border-black/5 dark:bg-gray-950/15 dark:border-white/10 dark:text-gray-300">
            <div class="flex items-center">
              <o-chip flat outline size="xs" class="mb-0 opacity-75 mr-2">
                <o-icon icon="searchEnter" :size="10" />
              </o-chip>
              <p>{{ $t('components.search.search.toSelect') }}</p>
            </div>
            <div class="flex items-center">
              <o-chip flat outline size="xs" class="mb-0 opacity-75 mr-px">
                <o-icon icon="searchArrowUp" :size="10" />
              </o-chip>
              <o-chip flat outline size="xs" class="mb-0 opacity-75 mr-2">
                <o-icon icon="searchArrowDown" :size="10" />
              </o-chip>
              <p>{{ $t('components.search.search.toNavigate') }}</p>
            </div>
            <div class="flex items-center">
              <o-chip flat outline size="xs" class="mb-0 opacity-75 mr-2">
                {{ $t('components.search.search.esc') }}
              </o-chip>
              <p>{{ $t('components.search.search.toClose') }}</p>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { useNuxtApp } from '#app'
import debounce from 'lodash/debounce'

import Settings from './provider/models/settings'
import Customer from './provider/models/customer'
import HelpGuide from './provider/models/helpGuide'

import { useUIStore } from '~/stores/useUIStore'
import { useAuthStore } from '~/stores/useAuthStore'

const router = useRouter()
const uiStore = useUIStore()
const authStore = useAuthStore()

const search = ref<any>()

// Order matters — settings ranks first because that's what a user is most
// likely to be looking for from the modal. Customer comes second so people
// reach a specific record quickly; help articles are last.
const models = reactive<Record<string, any>>({
  Settings,
  Customer,
  HelpGuide,
})

const query = ref('')
const state = reactive({
  selected: {
    model: 0,
    result: 0,
  },
  filtering: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

// Search state is stored in Pinia. Can be triggered from anywhere in app
const active = computed({
  get () {
    return uiStore.search
  },
  set (value: boolean) {
    uiStore.setSearch(value)
  },
})

// Compile all individual model results into single array
const results = computed(() => {
  const modelValues = Object.values(models)
  const arrays = modelValues.map((model: any) => model.results)
  const r = ([] as any[]).concat(...arrays)

  return r
})

let initFrame: number | null = null
let initTimer: number | null = null

const cancelScheduledInit = () => {
  if (initFrame !== null) {
    cancelAnimationFrame(initFrame)
    initFrame = null
  }
  if (initTimer !== null) {
    window.clearTimeout(initTimer)
    initTimer = null
  }
}

// Paint and focus the overlay before starting provider work. Scheduling the
// task from requestAnimationFrame lets the browser commit the opening frame
// first; settings indexing and cached-customer hydration then happen without
// delaying visual feedback from the click.
const scheduleInitAfterPaint = () => {
  cancelScheduledInit()

  initFrame = requestAnimationFrame(() => {
    initFrame = null
    initTimer = window.setTimeout(() => {
      initTimer = null
      if (active.value) void init()
    }, 0)
  })
}

watch(active, async (val) => {
  if (!val) {
    cancelScheduledInit()
    return
  }

  query.value = ''
  await nextTick()
  search.value?.focus()
  scheduleInitAfterPaint()
}, { flush: 'post' })

// Watch search query and trigger search
watch(query, (val) => {
  emit('update:modelValue', val)
  state.filtering = query.value !== ''
  debouncedSearch()
})

const resetSelectedResult = () => {
  const modelValues = Object.values(models)

  for (const index in modelValues) {
    const model = modelValues[index] as any

    if (model.results?.length) {
      state.selected.model = Number(index)
      state.selected.result = 0

      return
    }
  }

  state.selected.model = 0
  state.selected.result = 0
}

// Initialise the search models. We pass every plugin a model might need so
// they don't each have to call `useNuxtApp()`.
async function init () {
  const app = useNuxtApp()
  const locale = (app.$i18n as any)?.locale?.value ?? (app.$i18n as any)?.locale ?? ''
  const accountKey = authStore.activeGuid ?? ''
  const context = {
    $auth: app.$auth,
    $grants: app.$grants,
    $features: app.$features,
    $gates: app.$gates,
    $i18n: app.$i18n,
    accountKey,
    cacheKey: `${accountKey}:${locale}`,
  }

  const tasks: Promise<unknown>[] = []
  for (const model of Object.values(models)) {
    if ((model as any).init) {
      tasks.push(Promise.resolve((model as any).init(context)))
    }
  }

  await Promise.allSettled(tasks)

  // A user can begin typing while background providers are still loading.
  // Refresh once they settle so newly available customer/help results appear
  // without requiring another keypress.
  if (active.value && query.value) {
    runSearch(query.value)
  }
}

const runSearch = (searchQuery: string) => {
  if (searchQuery) {
    for (const model of Object.values(models)) {
      (model as any).search(searchQuery)
    }
  }

  resetSelectedResult()
  state.filtering = false
}

// Iterate through models and trigger each individual search process
const debouncedSearch = debounce(() => {
  runSearch(query.value)
}, 500)

onBeforeUnmount(() => {
  cancelScheduledInit()
  debouncedSearch.cancel()
})

// Close search box
function cancel () {
  active.value = false
  query.value = ''
}

// Change selected result using arrow keys by passing in the direction/increment
function moveIndex (increment = 1) {
  const modelValues = Object.values(models) as any[]
  const direction = Math.sign(increment) || 1

  if (results.value.length) {
    const selected = state.selected
    const modelIndex = selected.model
    const resultIndex = selected.result

    const nextResult = modelValues[modelIndex]?.results?.[resultIndex + direction]

    if (nextResult) {
      state.selected.result = resultIndex + direction
      return
    }

    for (let nextModelIndex = modelIndex + direction; nextModelIndex >= 0 && nextModelIndex < modelValues.length; nextModelIndex += direction) {
      const nextModel = modelValues[nextModelIndex]

      if (nextModel?.results?.length) {
        state.selected.model = nextModelIndex
        state.selected.result = direction === 1 ? 0 : nextModel.results.length - 1
        return
      }
    }
  }
}

// Manually set the selected result index
function setIndex (modelIndex: number | string, resultIndex: number | string) {
  state.selected.model = Number(modelIndex)
  state.selected.result = Number(resultIndex)
}

// Select the current result - trigger router
function select () {
  const selected = state.selected
  const modelIndex = selected.model
  const resultIndex = selected.result

  const model = Object.values(models)[modelIndex] as any
  const item = model?.results?.[resultIndex]

  if (item) {
    router.push(item.path)

    query.value = ''
    active.value = false
  }
}
</script>
